import React, { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { authService } from '../services/authService'
import { rememberAuthReason } from '@/services/api'
import { useLanguage } from '@/contexts/LanguageContext'
import { logError, toUserMessage } from '@/utils/errorMessage'

interface ProtectedRouteProps {
  children: React.ReactNode
}

/**
 * Oturum dogrulanamadiginda kullanici sessizce login ekranina atiliyordu ve
 * NEDENINI hic ogrenemiyordu. Yonlendirme davranisi (guvenlik) aynen korunur;
 * sebep `rememberAuthReason` ile saklanir, LoginScreen mount'ta okuyup gosterir.
 *
 * Sebep hem bellekte hem sessionStorage'da tutuldugu icin `authService.logout()`
 * icindeki `sessionStorage.clear()` onu artik silemez.
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { t } = useLanguage()
  const [isLoading, setIsLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // First check if there's a valid token locally
        const hasValidToken = authService.isTokenValid()
        const hasUser = authService.getCurrentUser() !== null

        if (!hasValidToken || !hasUser) {
          // Hic oturum acilmamissa mesaj gosterme (kullanici zaten cikis yapmis
          // olabilir); ama elde token/kullanici VARKEN gecersizse sebebi soyle.
          if (authService.getToken() !== null || hasUser) {
            rememberAuthReason(t.errors.sessionExpired)
          }
          authService.logout()
          setIsAuthenticated(false)
          setIsLoading(false)
          return
        }

        // Validate session with server
        const sessionResult = await authService.validateSession()
        
        if (sessionResult.success) {
          setIsAuthenticated(true)
        } else if (sessionResult.transient) {
          // Sunucuya ulasilamadi (ag/zaman asimi/sunucu hatasi) -- oturum GECERSIZ DEGIL.
          // Yerel token hala gecerli oldugu icin kullaniciyi disari atmiyoruz; anlik bir
          // baglanti kopmasinin oturumu dusurmesi hem kotu bir deneyim hem gereksiz.
          // Guvenlik acigi degil: sonraki her istek yine sunucuda dogrulanir ve
          // gercek bir 401 merkezi interceptor'da login'e yonlendirir.
          console.warn('[ProtectedRoute] Oturum dogrulanamadi (gecici):', sessionResult.message)
          setIsAuthenticated(true)
        } else {
          // Backend'in sebebi ('Token suresi dolmus', 'Oturumunuz sonlandirildi')
          // kutsaldir; yoksa yerel varsayilan metin kullanilir.
          rememberAuthReason(sessionResult.message?.trim() || t.errors.sessionExpired)
          authService.logout()
          setIsAuthenticated(false)
        }
        
      } catch (error) {
        // Beklenmeyen istisna: yine login'e atiyoruz ama sessizce degil.
        logError('ProtectedRoute.checkAuth', error)
        rememberAuthReason(toUserMessage(error, t, t.errors.sessionExpired))
        authService.logout()
        setIsAuthenticated(false)
      } finally {
        setIsLoading(false)
      }
    }
    
    checkAuth()
    // t degistiginde (dil degisimi) oturumu yeniden dogrulamaya gerek yok:
    // kontrol yalnizca mount'ta calisir, davranis eskisiyle ayni.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (isLoading) {
    // Auth doğrulanırken ayrı bir splash yerine dashboard iskeletini göster
    return (
      <div className="min-h-screen bg-gray-50 animate-pulse">
        {/* Header skeleton */}
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div>
                <div className="h-5 w-44 bg-gray-200 rounded mb-2"></div>
                <div className="h-3 w-24 bg-gray-100 rounded"></div>
              </div>
              <div className="hidden lg:flex items-center space-x-3">
                <div className="h-9 w-28 bg-gray-200 rounded-lg"></div>
                <div className="h-9 w-36 bg-gray-200 rounded-lg"></div>
                <div className="h-9 w-36 bg-gray-200 rounded-lg"></div>
                <div className="h-9 w-24 bg-gray-100 rounded-lg"></div>
                <div className="h-9 w-24 bg-gray-100 rounded-lg"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Content skeleton */}
        <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Filters row */}
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between mb-8">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="h-10 w-40 bg-gray-200 rounded-lg"></div>
              <div className="h-10 w-44 bg-gray-200 rounded-lg"></div>
              <div className="h-10 w-32 bg-gray-200 rounded-lg"></div>
              <div className="h-10 w-72 bg-gray-200 rounded-lg"></div>
            </div>
            <div className="flex items-center space-x-1">
              <div className="h-9 w-9 bg-gray-200 rounded-lg"></div>
              <div className="h-9 w-9 bg-gray-100 rounded-lg"></div>
            </div>
          </div>

          {/* Card grid skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-7">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white rounded-xl shadow-sm border p-6">
                <div className="flex items-start space-x-3 mb-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-lg flex-shrink-0"></div>
                  <div className="flex-1">
                    <div className="h-4 w-2/3 bg-gray-200 rounded mb-2"></div>
                    <div className="h-3 w-1/3 bg-gray-100 rounded"></div>
                  </div>
                </div>
                <div className="space-y-2 mb-4">
                  <div className="h-3 w-1/2 bg-gray-100 rounded"></div>
                  <div className="h-3 w-2/5 bg-gray-100 rounded"></div>
                </div>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="h-14 bg-gray-100 rounded-lg"></div>
                  <div className="h-14 bg-gray-100 rounded-lg"></div>
                  <div className="h-14 bg-gray-100 rounded-lg"></div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-8 flex-1 bg-gray-100 rounded-lg"></div>
                  <div className="h-8 flex-1 bg-gray-100 rounded-lg"></div>
                  <div className="h-8 w-9 bg-gray-100 rounded-lg"></div>
                  <div className="h-8 w-9 bg-gray-100 rounded-lg"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  return <>{children}</>
}

export default ProtectedRoute 